/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as design from '@constant/design';
import { useTypedSelector } from '@hook/common/useStore';
import React from 'react';
import { useAuthInput, useAuthPage, useSignUpAndIn } from '@hook/auth/useAuth';
import Gap from '@component/common/gap';
import TextBlock from 'component/common/block/text-block';
import ImageTextBlock from 'component/common/block/image-text-block';
import google from '@asset/image/google-logo.png';
import naver from '@asset/image/naver-logo.png';
import kakao from '@asset/image/kakao-logo.png';
import apple from '@asset/image/apple-logo.png';
import AuthBox from 'component/auth/auth-box';
import TextInput from '@component/common/input/text-input';
import { createAuthLogoText, createErrorText } from '@util/string';
import { createAuthLogoImage } from '@util/image';
import IconBlock from '@component/common/block/icon-block';
import { PASSWORD_INPUT_DESIGN } from '@constant/design';
import { SERVICE } from '@config/environment';
import { EAuthType, EService } from '@datatype/type';

const createOauthProviders = (): Exclude<EAuthType, EAuthType.INHOUSE>[] => {
  switch (SERVICE) {
    case EService.GRIDGIO:
      return [];
    case EService.IYAGIGO:
    case EService.MOASHELF:
    default:
      return [EAuthType.GOOGLE, EAuthType.NAVER, EAuthType.KAKAO, EAuthType.APPLE];
  }
};

const PasswordContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
`;

const SignInButtonContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const OauthSigninsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;

const SignTextContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${size.MARGIN_1};
`;

const SOCIAL_LOGIN_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: size.AUTH_BLOCK_WIDTH,
    height: size.AUTH_BLOCK_HEIGHT,
    gap: size.MARGIN_1,
    borderWidth: '1px',
    borderColor: color.GRAY_1,
    borderRadius: size.BORDER_RADIUS_2,
    event: { cursor: 'pointer' },
  },
  image: { height: '20px' },
} as const;

const FindText = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
`;

const Signin: React.FC = () => {
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;

  const { password, username } = useAuthInput();
  const { inhouseSignin, oauthPage } = useSignUpAndIn();
  const { openAuthPage } = useAuthPage();

  const handleSigninClick = () => inhouseSignin(SERVICE, username.value, password.value);
  const handleSignUpClick = () => openAuthPage('code');
  const handleFindIdClick = () => openAuthPage('findid');
  const handleFindPasswordClick = () => openAuthPage('findpw');

  const OauthSignins = createOauthProviders().map((provider) => {
    const oauthSigninProvider = {
      [EAuthType.GOOGLE]: { src: google, alt: 'google', text: '구글 아이디로 시작하기', height: '20px' },
      [EAuthType.NAVER]: { src: naver, alt: 'naver', text: '네이버 아이디로 시작하기', height: '25px' },
      [EAuthType.KAKAO]: { src: kakao, alt: 'kakao', text: '카카오 아이디로 시작하기', height: '26px' },
      [EAuthType.APPLE]: { src: apple, alt: 'apple', text: '애플 아이디로 시작하기', height: '26px' },
    };
    const item = oauthSigninProvider[provider];
    return (
      <ImageTextBlock
        key={provider}
        src={item.src}
        alt={item.alt}
        text={item.text}
        design={{ ...SOCIAL_LOGIN_DESIGN, image: { height: item.height } }}
        click={{ handler: () => oauthPage(provider), mode: 'throttle', delay: 3000 }}
      />
    );
  });

  return (
    <AuthBox design={{ width: size.AUTH_BOX_WIDTH }}>
      <ImageTextBlock
        src={createAuthLogoImage()}
        alt='otterbrick'
        text={createAuthLogoText()}
        design={design.AUTH_LOGO_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      <TextInput
        name={'username'}
        placeholder={'아이디'}
        design={design.AUTH_INPUT_DESIGN}
        onChange={username.handler}
        value={username.value}
      />
      <Gap height={size.MARGIN_1} />
      <PasswordContainer>
        <TextInput
          name={'password'}
          placeholder={'비밀번호'}
          design={PASSWORD_INPUT_DESIGN}
          onChange={password.handler}
          value={password.value}
          type={password.visible ? 'text' : 'password'}
        />
        <IconBlock
          icon={password.visible ? 'visibility-on' : 'visibility-off'}
          design={{
            icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
            box: { right: size.MARGIN_1, event: { cursor: 'pointer' } },
          }}
          click={{ handler: password.visibleHandler }}
        />
      </PasswordContainer>
      <TextBlock text={createErrorText(errorCode)} design={design.ERROR_TEXT_DESIGN} />
      <SignInButtonContainer>
        <TextBlock
          text='로그인'
          design={design.createAuthButtonDesign()}
          click={{ handler: handleSigninClick, mode: 'throttle', delay: 3000 }}
        />
        <SignTextContainer>
          <TextBlock text='회원가입' design={design.createSignTextDesign()} click={{ handler: handleSignUpClick }} />
          <FindText>
            <TextBlock text='아이디/' design={design.createSignTextDesign()} click={{ handler: handleFindIdClick }} />
            <TextBlock
              text='비밀번호 찾기'
              design={design.createSignTextDesign()}
              click={{ handler: handleFindPasswordClick }}
            />
          </FindText>
        </SignTextContainer>
      </SignInButtonContainer>
      {OauthSignins.length > 0 && (
        <>
          <Gap height={size.MARGIN_3} />
          <TextBlock
            text='또는 간편 로그인으로 시작해보세요'
            design={{ text: { size: 'small', bold: true, color: color.GRAY_1 } }}
          />
          <Gap height={size.MARGIN_1} />
          <OauthSigninsContainer>{OauthSignins}</OauthSigninsContainer>
        </>
      )}
    </AuthBox>
  );
};

export default Signin;
